import React, {useState} from 'react'
import {Link} from 'react-router-dom'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {OverflowFooter} from '../../../components/overflow'
import {
  ThemeDetailFragment,
  useDeleteThemeMutation,
  useSetDefaultThemeMutation
} from '../../../data/workbench-api/generated/theme'
import {useDuplicateTheme} from '../../../hooks/use-duplicate-theme'
import {useToast} from '../../../lib/toast'
import {DeleteLink} from '../components'
import copy from 'copy-to-clipboard'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {EnvironmentNode} from '../../../environment'

const connectedContainerCount = (theme: ThemeDetailFragment) =>
  theme.streamContainers.totalCount +
  theme.streamContainersByDarkThemeId.totalCount

export const Overflow = ({
  theme,
  editLink,
  closeDropdown,
  refetch,
  canEdit,
  environment
}: {
  theme: ThemeDetailFragment
  editLink: string
  closeDropdown: () => void
  refetch: () => void
  canEdit: boolean
  environment: EnvironmentNode
}) => {
  const toaster = useToast()
  const analytics = useAnalytics()
  const [deleteMutation] = useDeleteThemeMutation({
    onCompleted: () => {
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>sdk>container-themes'
        }
      })
    }
  })
  const [duplicateThemeMutation] = useDuplicateTheme()
  const [setDefaultMutation] = useSetDefaultThemeMutation({
    onCompleted: () => {
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>sdk>container-themes'
        }
      })
    }
  })
  const containerCount = connectedContainerCount(theme)
  const [copied, setCopied] = useState(false)

  return (
    <>
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <Link to={editLink}>Edit</Link>
        </DropdownItem>
        {!theme.isDefault ? (
          <DropdownItem disabled={!canEdit}>
            <a
              onClick={() => {
                setDefaultMutation({
                  variables: {
                    environmentId: theme.environmentId,
                    themeId: theme.id
                  }
                }).then(closeDropdown)
              }}
            >
              Make default
            </a>
          </DropdownItem>
        ) : null}
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              duplicateThemeMutation(theme).then(() => {
                toaster('Theme duplicated')
                refetch()
              })
              closeDropdown()
            }}
          >
            Duplicate
          </a>
        </DropdownItem>
        {!connectedContainerCount(theme) && !theme.isDefault ? (
          <DropdownItem disabled={!canEdit}>
            <DeleteLink
              onClick={() => {
                if (confirm('Deletion is permanent - are you sure?')) {
                  deleteMutation({
                    variables: {
                      id: theme.id,
                      environmentId: theme.environmentId
                    }
                  }).then(() => {
                    refetch()
                    closeDropdown()
                  })
                }
              }}
            >
              Delete
            </DeleteLink>
          </DropdownItem>
        ) : null}
        <DropdownItem>
          <a
            onClick={() => {
              if (copied) {
                return
              }

              copy(JSON.stringify(theme.payload))
              setCopied(true)
              setTimeout(() => setCopied(false), 800)
            }}
          >
            {copied ? 'Copied' : 'Copy to clipboard'}
          </a>
        </DropdownItem>
      </DropdownBody>

      <DropdownFooter>
        <OverflowFooter
          id={theme.id}
          created={theme.created}
          updated={theme.updated}
          rows={[`Connected containers: ${containerCount}`]}
        />
      </DropdownFooter>
    </>
  )
}
